/*
 * Exact rationals for the daily puzzle (ADR 0127 §2): a value is checked as a
 * reduced fraction, never as a float, so "0.1 × 30" and "¾ of 16" are compared
 * as what they are. Every value is kept reduced with a positive denominator.
 */
export interface Rational {
    n: number;
    d: number;
}

function gcd(a: number, b: number): number {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b) {
        [a, b] = [b, a % b];
    }
    return a;
}

export function rat(n: number, d: number = 1): Rational {
    if (d === 0) {
        throw new Error("Division by zero");
    }
    const sign = d < 0 ? -1 : 1;
    const divisor = gcd(n, d) || 1;
    return { n: (sign * n) / divisor, d: (sign * d) / divisor };
}

export const add = (a: Rational, b: Rational): Rational => rat(a.n * b.d + b.n * a.d, a.d * b.d);
export const sub = (a: Rational, b: Rational): Rational => rat(a.n * b.d - b.n * a.d, a.d * b.d);
export const mul = (a: Rational, b: Rational): Rational => rat(a.n * b.n, a.d * b.d);
export const div = (a: Rational, b: Rational): Rational => rat(a.n * b.d, a.d * b.n);

/** Whole-number power (the puzzle uses 2 to 5). */
export function pow(base: Rational, exponent: number): Rational {
    let result = rat(1);
    for (let i = 0; i < exponent; i++) {
        result = mul(result, base);
    }
    return result;
}

function exactRoot(n: number, index: number): number {
    const root = Math.round(Math.abs(n) ** (1 / index)) * (n < 0 ? -1 : 1);
    if (root ** index !== n || (n < 0 && index % 2 === 0)) {
        throw new Error(`${n} has no whole ${index === 2 ? "square" : "cube"} root`);
    }
    return root;
}

/** The square (index 2) or cube (index 3) root of a rational whose parts are perfect powers; throws otherwise. */
export function root(a: Rational, index: 2 | 3): Rational {
    return rat(exactRoot(a.n, index), exactRoot(a.d, index));
}

export const equals = (a: Rational, b: Rational): boolean => a.n === b.n && a.d === b.d;
export const isInteger = (a: Rational): boolean => a.d === 1;
export const isNegative = (a: Rational): boolean => a.n < 0;
export const abs = (a: Rational): Rational => ({ n: Math.abs(a.n), d: a.d });
export const toNumber = (a: Rational): number => a.n / a.d;
